<template>
  <div class="main-div">
    <div class="form-div">
      <VasionInput
        v-if="displayFirstEmailField"
        id="first-user-name"
        v-model="modifyUser.email"
        title="Email (Username)"
        class="input-style email-input"
        inputType="top-white"
        required
      />

      <VasionInput
        v-if="displayUsernameField"
        v-model="modifyUser.userName"
        title="Username"
        class="input-style"
        inputType="top-white"
        required
        :isInErrorState="formFieldErrors.username"
        @input="val => val ? clearFieldError('username') : null"
      />

      <VasionInput
        v-if="displayPassword"
        v-model="userPassword"
        title="Password"
        class="input-style"
        inputType="top-white"
        specialType="password"
        :canToggleShowPassword="false"
        required
        :isInErrorState="formFieldErrors.password"
        @input="val => val ? clearFieldError('password') : null"
        @blur="passwordInputOnBlur"
        @focus="passwordInputOnFocus"
        preventPasswordAutofill
      />

      <div class="name-section">
        <VasionInput
          v-model="modifyUser.firstName"
          title="First Name"
          class="input-style first-name-input"
          inputType="top-white"
          required
          :isInErrorState="formFieldErrors.firstName"
          @input="val => val ? clearFieldError('firstName') : null"
        />
        <VasionInput
          v-model="modifyUser.lastName"
          title="Last Name"
          class="input-style last-name-input"
          inputType="top-white"
        />
      </div>

      <VasionInput
        v-model="modifyUser.middleName"
        title="Middle Name"
        class="input-style"
        inputType="top-white"
      />

      <div class="email-phone-section">
        <VasionInput
          v-if="displayEmailField"
          v-model="modifyUser.email"
          title="Email"
          class="input-style email-input"
          inputType="top-white"
        />
        <VasionInput
          v-model="modifyUser.mobilePhone"
          title="Mobile"
          class="input-style mobile-input"
          inputType="top-white"
        />
      </div>

      <div v-if="displayDepartmentField" class="dept-section">
        <VasionDropList
          v-slot="slotItem"
          v-model="selectedDepartment"
          :dataArray="departmentList"
          title="Department"
          class="droplist-style department-drop"
          :type="isAdminUser ? 'add-and-delete': 'plain-list'"
          displayName="sName"
          valueName="sName"
          :required="true"
          :showSearchField="false"
          :showAddItem="true"
          :addItemText="'New Department'"
          :forceOpen="forceOpenDepartmentDroplist"
          overlayList
          @addItemClicked="showAddDepartment = true"
          @deleteClicked="deleteDepartmentClicked"
        >
          {{ slotItem.item.sName }}
        </VasionDropList>
      </div>

      <div v-if="displayDepartmentAdminField" class="md-layout">
        <VasionCheckbox
          id="dept-admin"
          name="dept-admin"
          :checked="isModUserDeptAdmin"
          @change="toggleDepartmentAdmin()"
        >
          Department Admin
        </VasionCheckbox>
      </div>
      <div v-if="isAdminUser && isModUserDeptAdmin" class="dept-admin-section">
        <VasionDropList
          v-slot="slotItem"
          v-model="selectedAdminDepartments"
          :dataArray="departmentList"
          :multiSelectInitialValue="selectedAdminDepartments"
          title="Department Admin of"
          class="droplist-style department-drop"
          type="check-list-search"
          displayName="sName"
          valueName="iID"
          openAbove
          overlayList
        >
          {{ slotItem.item.sName }}
        </VasionDropList>
      </div>

      <div v-if="displayLoginTypeField" class="misc-section">
        <VasionDropList
          v-slot="slotItem"
          v-model="selectedLoginType"
          :dataArray="loginTypeList"
          title="Login Type"
          class="droplist-style login-type-drop"
          type="plain-list"
          displayName="sName"
          valueName="sName"
          openAbove
        >
          {{ slotItem.item.sName }}
        </VasionDropList>
      </div>

      <div class="md-layout">
        <div v-if="displayActiveField" class="checkbox-div">
          <VasionCheckbox
            id="active-user"
            name="active-user"
            :checked="modifyUser.active"
            @change="toggleActiveUser()"
          >
            Active
          </VasionCheckbox>
        </div>
        <div v-if="displayAdminField" class="checkbox-div">
          <VasionCheckbox
            id="admin-user"
            name="admin-user"
            :checked="modifyUser.admin"
            @change="toggleAdminUser()"
          >
            Admin
          </VasionCheckbox>
        </div>
      </div>

      <div v-if="displayPortalGroupsField">
        <VasionDropList
          v-slot="slotItem"
          v-model="portalGroupsSelected"
          title="Initial User Groups"
          class="droplist-style"
          type="check-list-search"
          displayName="name"
          valueName="value"
          width="100%"
          :dataArray="portalGroups"
          :showIfEmpty="true"
          openAbove
        >
          {{ slotItem.item.name }}
        </VasionDropList>
      </div>

      <div v-if="displayStorageFolderField">
        <VasionDropList
          v-slot="slotItem"
          v-model="storageFolder"
          title="Initial Default Storage Folder"
          class="droplist-style"
          type="plain-list"
          displayName="name"
          valueName="value"
          width="100%"
          :dataArray="storageFoldersList"
          :showIfEmpty="true"
          openAbove
        >
          {{ slotItem.item.name }}
        </VasionDropList>
      </div>

      <div class="bottom-div">
        <VasionButton id="cancel-button" :classProp="'primary-light'" @vasionButtonClicked="cancelClick()">
          CANCEL
        </VasionButton>
        <VasionButton id="ok-button" :classProp="'primary'" @vasionButtonClicked="okClick(false)">
          OK
        </VasionButton>
      </div>
    </div>

    <AddDepartmentModal
      :show="showAddDepartment"
      @close="showAddDepartment = false"
      @departmentAdded="departmentAdded"
    />

    <md-dialog id="vasion-confirmation-div" :md-active.sync="showDeleteDepartmentConfirmation" :md-click-outside-to-close="false">
      <VasionConfirmationDialog
        :message="deleteDepartmentConfirmationMessage"
        @noButtonClick="showDeleteDepartmentConfirmation=false"
        @yesButtonClick="deleteDepartment"
      />
    </md-dialog>

    <VasionGeneralModal
      id="convert-user-modal"
      :hideButtons="true"
      :modalType="'slot'"
      buttonGroup="single"
      :sync="showConvertExternalUser"
      :modalTitle="'Duplicate User Found'"
      @noButtonClick="showLeaveDialog = false"
    >
      <VasionConfirmationDialog
        :message="convertExternalUserMessage"
        :yesButtonText="'Yes, Convert User'"
        @noButtonClick="showConvertExternalUser=false"
        @yesButtonClick="convertExternalUser"
      />
    </VasionGeneralModal>

    <VasionSnackbar
      :showSnackbarBool.sync="snackbar.show"
      :snackbarImage="snackbar.image"
      :snackbarSubTitle="snackbar.subtitle"
      :snackbarTitle="snackbar.title"
    />
  </div>
</template>

<script>
import AddDepartmentModal from '@/components/security/AddDepartmentModal.vue';
const PasswordPlaceholder = '790bbf9a16b84c6bb243'

export default {
  name: 'ConfigureUser',
  components: {
    AddDepartmentModal
  },
  props: {
    modifyUserId: {
      type: Number,
      default: 0,
      required: false,
    },
    currentUser: {
      type: Object,
      default: () => { return {
        adminForDepartments: null,
        admin: true,
        active: true,
        department: null,
        email: "",
        firstName: "",
        lastName: "",
        loginType: null,
        middleName: "",
        mobilePhone: "",
        passwordIsSet: true,
        userName: "",
        userID: null,
      }},
      required: true,
    },
    requestedUserType: {
      type: String,
      default: 'internalUsers',
    },
  },
  data: function () {
    return {
      currentUserType: this.requestedUserType,
      forceOpenDepartmentDroplist: false,
      formFieldErrors: {
        username: false,
        password: false,
        firstName: false,
      },
      isModUserDeptAdmin: false,
      modifyUser: {},
      portalGroupsSelected: [],
      selectedDepartment: {
        iID: 0,
        sName: '',
      },
      selectedAdminDepartments: [],
      selectedDepartmentForDelete: null,
      selectedLoginType: {
        iID: 0,
        sName: '',
      },
      showAddDepartment: false,
      showConvertExternalUser: false,
      showDeleteDepartmentConfirmation: false,
      snackbar: {
        show: false,
        image: false,
        subtitle: '',
        title: ''
      },
      storageFolder: null,
      userPassword: ''
    }
  },
  computed: {
    convertExternalUserMessage() {
      return `This user is already registered as an external signer. To proceed, you must convert them into a portal user. Would you like to convert this user now?`
    },
    currentUserIsDepartmentAdmin() { return this.currentUser?.adminForDepartments?.length > 0 && !this.currentUser?.admin },
    deleteDepartmentConfirmationMessage() { return `Are you sure you want to delete this department: '${this.selectedDepartmentForDelete?.sName}'?` },
    departmentList() { return this.$store.state.security.departmentList },
    displayActiveField() {
      return this.isInternalUserRequested || this.isPortalUserRequested
    },
    displayAdminField() {
      return this.isInternalUserRequested && !this.currentUserIsDepartmentAdmin
    },
    displayDepartmentAdminField() {
      return this.isInternalUserRequested && this.isAdminUser
    },
    displayDepartmentField() {
      return this.isInternalUserRequested
    },
    displayEmailField() {
      return this.isInternalUserRequested
    },
    displayFirstEmailField() {
      return this.isPortalUserRequested
    },
    displayLoginTypeField() {
      return this.isInternalUserRequested
    },
    displayPassword() { return this.isInternalUserRequested && !(this.currentUserIsDepartmentAdmin && this.modifyUser.admin) },
    displayPortalGroupsField() { return this.isPortalUserRequested && !this.isEditMode },
    displayStorageFolderField() { return this.isPortalUserRequested && !this.isEditMode },
    displayUsernameField() { return this.isInternalUserRequested } ,
    folderList() { return this.$store.state.vault.vaultList },
    portalGroups() { return this.$store.state.common.groups },
    isAdminUser() { return this.$store.getters['common/isAdminUser'] },
    isEditMode() { return this.modifyUserId > 0 },
    isInternalUserRequested() {
      return this.requestedUserType === 'internal'
    },
    isPortalUserRequested() {
      return this.requestedUserType === 'portal'
    },
    loginTypeList() { return this.$store.state.security.loginTypeList },
    storageFoldersList() {
      if (!this.folderList || this.folderList.length === 0) return []

      const folderTypeForPortal = 5
      return this.folderList.filter(folder => folder.folderType === folderTypeForPortal).map((folder) => {
        return {
          name: folder.vaultName.trim(),
          value: folder.vaultID,
        }
      })
    },
  },
  mounted: async function () {
    if(this.isPortalUserRequested) {
      await this.$store.dispatch('common/getGroups', { onlyPortalGroups: true })
      await this.$store.dispatch('vault/getVaultList')
    }

    this.modifyUser = {
      admin: false,
      active: true,
      department: 1,
      email: '',
      firstName: '',
      lastName: '',
      loginType: 1,
      middleName: '',
      mobilePhone: '',
      password: '',
      userName: '',
      passwordIsSet: false,
      groups: null,
      storageFolder: null,
      IsPortalUser: false,
    }

    if (this.departmentList.length > 0) {
      this.selectedDepartment = this.departmentList[0]
    }

    if (this.loginTypeList.length > 0) {
      this.selectedLoginType = this.loginTypeList.find((x) => {
        return x.sName.toLowerCase() == "vasion login"
      })
    }

    if (this.modifyUserId <= 0) {
      return
    }

    this.modifyUser = await this.$store.dispatch('security/getUserDetails', this.modifyUserId)
    this.isModUserDeptAdmin = this.modifyUser.adminForDepartments && this.modifyUser.adminForDepartments.length > 0
    this.selectedAdminDepartments = this.isModUserDeptAdmin ? this.departmentList.filter(d => this.modifyUser.adminForDepartments.includes(d.iID)) : []

    if (this.modifyUser) {
      this.userPassword = this.modifyUser.passwordIsSet ? PasswordPlaceholder : ''
      this.modifyUser.IsPortalUser = this.modifyUser?.IsPortalUser ? this.modifyUser.IsPortalUser : false
    }

    const foundDepartment = this.departmentList.find((department) => {
      return department.iID === this.modifyUser.department
    })

    if (foundDepartment) {
      this.selectedDepartment = foundDepartment
    }

    const foundLoginType = this.loginTypeList.find((loginType) => {
      return loginType.iID === this.modifyUser.loginType
    })

    if (foundLoginType) {
      this.selectedLoginType = foundLoginType
    }
  },
  methods: {
    cancelClick() {
      this.$emit('cancel-click')
    },
    clearFieldError(field) {
      this.formFieldErrors[field] = false
    },
    clearFieldErrors() {
      Object.keys(this.formFieldErrors).map(key => this.formFieldErrors[key] = false)
    },
    convertExternalUser() {
      this.showConvertExternalUser = false
      this.okClick(true)
    },
    async okClick(convertToPortalUser) {
      this.clearFieldErrors()
      let errorText = ''

      if (this.displayFirstEmailField) {
        const email = this.modifyUser.email?.trim() || ''

        if (!email) {
          this.formFieldErrors.email = true
          errorText += 'Email (Username) cannot be empty.\n'
        } else {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          if (!emailRegex.test(email)) {
            this.formFieldErrors.email = true
            errorText += 'Please enter a valid email address.\n'
          }
        }
      }

      if (this.displayUsernameField && !this.modifyUser.userName) {
        this.formFieldErrors.username = true
      } else if (this.displayUsernameField && this.modifyUser.userName.trim() === '') {
        errorText += 'Username cannot only include spaces.\n'
      }

      if (this.displayPassword) {
        if (!this.userPassword) {
          this.formFieldErrors.password = true
        } else if (this.userPassword.trim() === '') {
          errorText += 'Password cannot only include spaces.\n'
        }
      }

      if (!this.modifyUser.firstName) {
        this.formFieldErrors.firstName = true
      } else if (this.modifyUser.firstName.trim() === '') {
        errorText += 'First Name cannot only include spaces.\n'
      }

      if(this.displayDepartmentField && (this.selectedDepartment?.iID ?? 0) === 0) {
        errorText += 'Please select a department.\n'
      }

      if (Object.values(this.formFieldErrors).some(x => x)) {
        errorText += 'Please fill out all required fields.'
      }

      if (errorText) {
        this.snackbar = {
          show: true,
          image: false,
          title: 'Error',
          subtitle: errorText,
        }
        return
      }

      this.modifyUser.department = this.selectedDepartment.iID
      if (this.isAdminUser) {
        if (this.isModUserDeptAdmin && this.selectedAdminDepartments.length > 0) {
          this.modifyUser.adminForDepartments = this.selectedAdminDepartments.map(dept => dept.iID)
        } else {
          this.modifyUser.adminForDepartments = null
        }
      }

      this.modifyUser.loginType = this.selectedLoginType && this.selectedLoginType.iID > 0 ? this.selectedLoginType.iID : 1

      this.modifyUser.password = this.userPassword == PasswordPlaceholder ? '' : this.userPassword

      if (this.isPortalUserRequested) {
        this.modifyUser.userName = this.modifyUser.email
        this.modifyUser.department = null
        this.modifyUser.loginType = 1
        this.modifyUser.IsPortalUser = true
        this.modifyUser.convertToPortalUser = convertToPortalUser
        this.modifyUser.groups = this.portalGroupsSelected.map(group => group.value)
        this.modifyUser.storageFolder = this.storageFolder?.value ?? null
      } else {
        this.modifyUser.groups = null
        this.modifyUser.storageFolder = null
      }

      const saveResult = await this.$store.dispatch('security/saveUserDetails', this.modifyUser)

      if (saveResult?.ResultMessage) {
        if (saveResult.ResultMessage.includes('already exists as an external signature user')) {
          this.showConvertExternalUser = true
          return
        }
        this.snackbar = {
          show: true,
          image: false,
          title: 'Error',
          subtitle: 'Unable to save User Information.' + saveResult.ResultMessage
        }
        return
      } else if (!saveResult || !saveResult.userID) {
        this.snackbar = {
          show: true,
          image: false,
          title: 'Error',
          subtitle: 'Unable to save User Information. Check logs.'
        }
        return
      }

      this.$emit('ok-click')
    },
    deleteDepartmentClicked(department){
      this.selectedDepartmentForDelete = department
      this.showDeleteDepartmentConfirmation = true
    },
    async deleteDepartment() {
      const department = this.selectedDepartmentForDelete
      const response = await this.$store.dispatch('security/deleteDepartment', { Value: department.iID.toString() })
      if (response?.Value?.length > 0 || response?.ResultStatus === 2) {
        this.snackbar = {
          show: true,
          image: false,
          title: 'Error',
          subtitle: response?.Value ?? `Error deleting department '${department.sName}'`
        }
      }
      else {
        this.snackbar = {
          show: true,
          image: true,
          title: 'Success',
          subtitle: `Department '${department.sName}' deleted`
        }
        if(this.selectedDepartment.iID === department.iID) {
          this.selectedDepartment = {
            iID: 0,
            sName: '',
          }
        }
        await this.$store.dispatch('security/loadDepartmentList')
      }

      this.showDeleteDepartmentConfirmation = false
      this.selectedDepartmentForDelete = null
    },
    departmentAdded(departmentName){
      const department = this.departmentList.find(x => x.sName === departmentName)
      if (department) {
        this.selectedDepartment = department
      }
      this.forceOpenDepartmentDroplist = true
      this.$nextTick(() => {
        this.forceOpenDepartmentDroplist = false
      })
    },
    passwordInputOnBlur() {
      if (this.modifyUser.passwordIsSet && this.userPassword === '') {
        this.userPassword = PasswordPlaceholder
      }
    },
    passwordInputOnFocus() {
      if (this.userPassword === PasswordPlaceholder) {
        this.userPassword = ''
      }
    },
    toggleActiveUser() {
      this.modifyUser.active = !this.modifyUser.active
    },
    toggleAdminUser() {
      this.modifyUser.admin = !this.modifyUser.admin
    },
    toggleDepartmentAdmin() {
      this.isModUserDeptAdmin = !this.isModUserDeptAdmin
    },
  },
}
</script>

<style lang="scss" scoped>
  .main-div {
    width: 480px;
    border: unset;
  }

  .form-div {
    display: flex;
    flex-direction: column;

    .name-section {
      display: flex;

      .first-name-input {
        flex: 1;
        margin-right: 12px;
      }
      .last-name-input {
        flex: 1;
      }
    }

    .email-phone-section {
      display: flex;
      .email-input {
        flex: 3;
        margin-right: 12px;
      }
      .mobile-input {
        flex: 2;
      }
    }

    .dept-section {
      display: flex;

      .department-drop {
        flex: 1;
      }
    }
    .dept-admin-section {
      text-align: left;
      display: flex;

      .department-drop {
        flex: 1;
      }
    }
    .misc-section {
      display: flex;

      .login-type-drop {
        flex: 1;
      }
    }
  }

  .bottom-div {
    text-align: right;
    vertical-align: middle;
    line-height: 50px;
  }

  .checkbox-div {
    margin-top: 16px;
    padding-right: 10px;
  }

  .input-style, .droplist-style {
    margin-top: 5px;
    margin-bottom: 5px;
  }

  #vasion-confirmation-div {
    z-index: 1001 !important;
  }
</style>
